import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { loadingStart, loadingStop, login } from 'redux/action';
import { toast } from 'react-toastify';
import { DashboardContainer, Table } from 'components';
import Body from './Body';
import API from 'services/api';
import moment from 'moment';
import { useNavigate, useLocation } from 'react-router-dom';

const Index = () => {
  const dispatch = useDispatch();
  const [franchiseOptions, setFranchiseOptions] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [tableData, setTableData] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const lastMonth = moment().subtract(1, 'months').format('MMMM');
  const [selectedMonth, setSelectedMonth] = useState({ label: lastMonth, value: lastMonth });
  const [selectedFranchise, setSelectedFranchise] = useState({ label: 'All', value: 'All' });
  const [sendEmailModalVisibility, setSendEmailModalVisibility] = useState(false);
  const [addEmailData, setAddEmailData] = useState({});
  const [employeeDataToMail, setEmployeeDataToMail] = useState([]);
  const [emailDataToSend, setEmailDataToSend] = useState({
    subject: 'You’re in the Red Zone: Let’s Get Back on Track with HSPP',
    body: `<p>Hii <strong>{{employee_name}}</strong>,</p>

    <table style="width: 100%; border-collapse: collapse; margin-top: 1em;">
  <thead>
    <tr>
      <th style="border: 1px solid #ddd; padding: 4px; text-align: center; font-size: 10px;">Location Name</th>
      <th style="border: 1px solid #ddd; padding: 4px; text-align: center; font-size: 10px;">HSPP Patients</th>
      <th style="border: 1px solid #ddd; padding: 4px; text-align: center; font-size: 10px;">Units Sold</th>
      <th style="border: 1px solid #ddd; padding: 4px; text-align: center; font-size: 10px;">Patients Sold</th>
    </tr>
  </thead>
  <tbody>
  {{tableData}}
  </tbody>
</table>

<p>Your location is currently in the <strong>HSPP Red Zone</strong>, meaning you're below the expected benchmarks:</p>

<ul style="margin: 1.5em; padding-left: 1em; list-style-type: disc;">
  <li><strong>50% attachment rate</strong> for stores selling under 10 hearing aids/month</li>
  <li><strong>10+ HSPPs/month</strong> for higher-volume locations</li>
</ul>


<p>
  The Health Services Protection Plan isn’t just an add-on — it’s a core part of how we care for our patients and build long-term trust. It also reflects your commitment to full-service excellence.
</p>

<p>
  We’re tracking this closely and expect every location to meet or exceed the goal. If you’re not there yet, now’s the time to refocus. Your manager is here to help if you need support.
</p>

<p><strong>Let’s get out of the Red Zone and finish strong. We believe in you.</strong></p>

<p style="margin-top: 1.5em;">— <strong>Regards,</strong><br/><strong>Health Services Protection Plan</strong></p>
`
  });

  const generateEmailTemplate = (employee_name, employee_locations) => {
    let emailTemplate = emailDataToSend.body;

    // Generate all rows for the employee's locations
    const rowsHTML = employee_locations
      .map((location) => {
        const hsppData = tableData.find((row) => row.location_id === location.location_id);
        const hspp_patients = hsppData?.active_hspp_patients || 0;
        const units_sold = hsppData?.total_units || 0;
        const patients_sold = hsppData?.unique_patients || 0;

        return `
        <tr>
          <td style="border: 1px solid #ddd; padding: 4px; font-size: 10px; text-align:center;">${location.location_name}</td>
          <td style="border: 1px solid #ddd; padding: 4px; font-size: 10px; text-align:center;">${hspp_patients}</td>
          <td style="border: 1px solid #ddd; padding: 4px; font-size: 10px; text-align:center;">${units_sold}</td>
          <td style="border: 1px solid #ddd; padding: 4px; font-size: 10px; text-align:center;">${patients_sold}</td>
        </tr>
      `;
      })
      .join('');

    // Inject dynamic rows into the template
    emailTemplate = emailTemplate.replace('{{tableData}}', rowsHTML);

    // Optionally replace employee_name if you add {{employee_name}} placeholder
    emailTemplate = emailTemplate.replace('{{employee_name}}', employee_name);

    return emailTemplate;
  };

  //--------------------------------------------------------code for selecting emails---------------------------------------------------------//
  const [selectedRows, setSelectedRows] = useState([]);
  const isRowSelected = (location_id) =>
    selectedRows.some((row) => row.location_id === location_id);

  const toggleRow = (location_id, location_name, active_hspp_patients) => {
    const exists = isRowSelected(location_id);

    if (exists) {
      setSelectedRows((prev) => prev.filter((r) => r.location_id !== location_id));
    } else {
      setSelectedRows((prev) => [
        ...prev,
        {
          location_id: location_id,
          location_name: location_name,
          active_hspp_patients: active_hspp_patients
        }
      ]);
    }
  };

  const toggleSelectAll = () => {
    if (selectedRows.length === tableData.length) {
      setSelectedRows([]);
    } else {
      const allRows = tableData.map((row) => ({
        location_id: row.location_id,
        location_name: row.location_name,
        active_hspp_patients: row.active_hspp_patients
      }));
      setSelectedRows(allRows);
    }
  };

  const isAllSelected = selectedRows.length === tableData.length;

  useEffect(() => {
    if (selectedRows.length > 0) {
      const locationIds = selectedRows.map((row) => row.location_id);
      getEmployeeDataForMailing(locationIds);
    } else {
      setEmployeeDataToMail([]);
    }
  }, [selectedRows]);

  // -------------------------------------------------------------------------------------------------------------------------------------------------

  useEffect(() => {
    getFranchiseLocation();
  }, []);

  useEffect(() => {
    init();
  }, [selectedMonth, selectedFranchise]);

  useEffect(() => {
    if (searchInput == '') {
      setTableData(originalData);
    } else {
      const filteredResults = originalData.filter((data) =>
        Object.values(data).join(', ').toLowerCase().includes(searchInput.toLowerCase())
      );
      setTableData(filteredResults);
    }
  }, [searchInput]);

  const getFranchiseLocation = () => {
    API.getFranchiseLocation().then((response) => {
      if (response) {
        const result = Array.from(
          new Map(
            response
              .filter((item) => item.franchiseId !== 'CF0049') // <-- Excluding Health Services California, LLC
              .map((item) => [
                item.franchiseId,
                { label: item.franchiseName, value: item.franchiseId }
              ])
          ).values()
        ).sort((a, b) => a.label.localeCompare(b.label));

        setFranchiseOptions(result);
      }
    });
  };

  const init = () => {
    dispatch(loadingStart());
    API.getRedZoneStoreReport({
      month: selectedMonth.value,
      franchise: selectedFranchise.value
    })
      .then((response) => {
        if (response) {
          const onlyRedZone = response.filter((item) => item.zone == 'red');
          setTableData(onlyRedZone);
          setOriginalData(response);
        }
      })
      .finally(() => {
        setSearchInput('red');
        dispatch(loadingStop());
      });
  };

  const getMonthOptions = () => {
    const currentMonthIndex = moment().month();
    const months = moment.months();
    return months.slice(0, currentMonthIndex).map((name) => ({
      label: name,
      value: name
    }));
  };

  const getEmployeeDataForMailing = (location_ids) => {
    dispatch(loadingStart());
    API.fetchEmployeeDataForRedZoneReport(location_ids)
      .then((response) => {
        if (response) {
          setEmployeeDataToMail(response);
        }
      })
      .finally(() => {
        dispatch(loadingStop());
      });
  };

  const handleEmailSubmit = () => {
    if (emailDataToSend.subject == '' || emailDataToSend.body == '') {
      return toast.error('Email Subject or Email Body cannot be blank.');
    }

    if (employeeDataToMail.length == 0) {
      return toast.error('No email ids found. Please add one.');
    }
    const finalDataToSubmit = employeeDataToMail.map((item) => {
      return {
        email: item.employee_email,
        name: item.employee_name,
        subject: emailDataToSend.subject,
        body: generateEmailTemplate(item.employee_name, item.employee_locations)
      };
    });

    dispatch(loadingStart());
    API.sendRedZoneReportEmail(finalDataToSubmit)
      .then((response) => {
        if (response) {
          setSendEmailModalVisibility(false);
          setEmployeeDataToMail([]);
          setSelectedRows([]);

          toast.success('Email sent successfully.');
        }
      })
      .finally(() => {
        dispatch(loadingStop());
      });
  };
  return (
    <DashboardContainer>
      <Body
        _this={{
          init,
          tableData,
          searchInput,
          setSearchInput,
          getMonthOptions,
          selectedMonth,
          setSelectedMonth,
          franchiseOptions,
          selectedFranchise,
          setSelectedFranchise,
          sendEmailModalVisibility,
          setSendEmailModalVisibility,
          emailDataToSend,
          setEmailDataToSend,
          handleEmailSubmit,
          getEmployeeDataForMailing,
          toggleRow,
          isRowSelected,
          toggleSelectAll,
          selectedRows,
          isAllSelected,
          employeeDataToMail,
          setEmployeeDataToMail,
          addEmailData,
          setAddEmailData
        }}
      />
    </DashboardContainer>
  );
};

export default Index;
